/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: known */

import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CheckIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Heading, headingVariants, Text } from "@/components/ui/typography";
import { useRequireRole } from "@/lib/role-hooks";
import { getDowngradeToFreeState } from "@/lib/subscription-actions";
import {
  describeBillingLabel,
  describeSiteUsageLabel,
  isPerActiveSitePlan,
} from "@/lib/subscription-display";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";

function isInfinite(value: number) {
  return !Number.isFinite(value);
}

function formatLimit(limit: number) {
  if (isInfinite(limit)) {
    return "&#8734;"; // infinity symbol
  }
  return limit.toLocaleString("en-GB");
}

function SubscriptionOverviewSkeleton() {
  return (
    <PageContainer className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <CardDescription>
            <Skeleton className="h-4 w-56" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-40" />
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function PlanSelectionSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: count }, (_, index) => (
          <div
            key={`plan-selection-skeleton-${index + 1}`}
            className="surface-panel-lg flex flex-col gap-6 p-8"
          >
            <div className="space-y-4">
              <Skeleton className="h-3 w-20" />
              <div className="space-y-3">
                <div className="flex items-end gap-3">
                  <Skeleton className="h-14 w-28" />
                  <Skeleton className="h-4 w-40" />
                </div>
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
            <div className="space-y-3 flex-1">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-10/12" />
              <Skeleton className="h-4 w-9/12" />
            </div>
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessRestrictedMessage() {
  return (
    <PageContainer className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Access restricted</CardTitle>
          <CardDescription>
            Only site managers can view subscription and billing information.
          </CardDescription>
        </CardHeader>
      </Card>
    </PageContainer>
  );
}

function PilotBypassAlert({
  isActive,
  pilotGrantorEmail,
  pilotExpiryDisplay,
  pilotReason,
}: {
  isActive: boolean;
  pilotGrantorEmail: string | null;
  pilotExpiryDisplay: string | null;
  pilotReason: string | null;
}) {
  if (!isActive) {
    return null;
  }

  return (
    <Alert variant="warning">
      <AlertTitle>Details</AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          <p>Granted by {pilotGrantorEmail ?? "an administrator"}.</p>
          <p>
            <span className="font-bold">Expiry:</span>{" "}
            {pilotExpiryDisplay ? ` ${pilotExpiryDisplay}` : "not set"}
          </p>
          {pilotReason ? (
            <p>
              <span className="font-bold">Reason:</span> {pilotReason}
            </p>
          ) : null}
          <p className="border-t pt-2 text-xs text-muted-foreground">
            The override will revert to standard billing after the expiry date
            or when removed by an admin.
          </p>
        </div>
      </AlertDescription>
    </Alert>
  );
}

function ScheduledCancellationAlert({
  hasActiveSubscription,
  hasScheduledCancellation,
  scheduledCancellationDateDisplay,
}: {
  hasActiveSubscription: boolean;
  hasScheduledCancellation: boolean;
  scheduledCancellationDateDisplay: string | null;
}) {
  if (!hasActiveSubscription || !hasScheduledCancellation) {
    return null;
  }

  return (
    <Alert variant="warning">
      <AlertTitle>Cancellation scheduled</AlertTitle>
      <AlertDescription>
        {scheduledCancellationDateDisplay
          ? `Your current subscription stays active until ${scheduledCancellationDateDisplay}. It will then move to Free unless you reactivate it in billing before then.`
          : "Your current subscription is set to end at the close of the current billing period unless you reactivate it in billing before then."}
      </AlertDescription>
    </Alert>
  );
}

function CoreTrialAlert({
  isVisible,
  trialExpiryDisplay,
}: {
  isVisible: boolean;
  trialExpiryDisplay: string | null;
}) {
  if (!isVisible) {
    return null;
  }

  return (
    <Alert>
      <AlertTitle>Core trial active</AlertTitle>
      <AlertDescription>
        {trialExpiryDisplay
          ? `Your Core trial runs until ${trialExpiryDisplay}. Subscribe to Base or Core to keep paid plan access after the trial.`
          : "Your Core trial is active. Subscribe to Base or Core to keep paid plan access after the trial."}
      </AlertDescription>
    </Alert>
  );
}

export default function BillingOverview() {
  const { hasAccess, isLoading: isRoleLoading } =
    useRequireRole("site_manager");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutState = searchParams.get("state");
  const checkoutSessionId = searchParams.get("session_id");
  const shouldVerifyCheckout =
    checkoutState === "checkout_success" && Boolean(checkoutSessionId);

  const availablePlansQuery = useQuery({
    ...trpc.getAvailablePlans.queryOptions(),
    enabled: hasAccess,
  });

  const subscriptionOverviewOptions =
    trpc.getSubscriptionOverview.queryOptions();

  const subscriptionOverviewQuery = useQuery({
    ...subscriptionOverviewOptions,
    enabled:
      hasAccess &&
      ((subscriptionOverviewOptions.enabled as boolean | undefined) ?? true),
  });
  const refetchSubscriptionOverview = subscriptionOverviewQuery.refetch;

  const { mutate: openBillingPortal, isPending: isBillingPortalPending } =
    useMutation({
      ...trpc.createBillingPortalSession.mutationOptions(),
      onSuccess: (session) => {
        if (typeof window !== "undefined") {
          const newWindow = window.open(
            session.url,
            "_blank",
            "noopener,noreferrer"
          );
          if (!newWindow) {
            toast.success("Billing portal link ready", {
              description: "Popup blocked. Allow popups to open the portal.",
            });
          }
        }
      },
      onError: (error) => {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to open billing portal";
        toast.error(message);
      },
    });

  const { mutate: openCoreUpgrade, isPending: isCoreUpgradePending } =
    useMutation({
      ...trpc.createCoreUpgradePortalSession.mutationOptions(),
      onSuccess: (session) => {
        if (typeof window !== "undefined") {
          window.location.href = session.url;
        }
      },
      onError: (error) => {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to start the Core upgrade";
        toast.error(message);
      },
    });

  const { mutate: startCheckout, isPending: isCheckoutPending } = useMutation({
    ...trpc.createCheckoutSession.mutationOptions(),
    onSuccess: (session) => {
      if (typeof window !== "undefined") {
        window.location.href = session.url;
      }
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to start subscription checkout";
      toast.error(message);
    },
  });

  const { mutate: scheduleDowngrade, isPending: isDowngradePending } =
    useMutation({
      ...trpc.scheduleFreeDowngrade.mutationOptions(),
      onSuccess: () => {
        toast.success("Downgrade scheduled", {
          description: "Your plan will switch to Free at the end of the cycle.",
        });
        void refetchSubscriptionOverview();
      },
      onError: (error) => {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to schedule the downgrade";
        toast.error(message);
      },
    });

  const handleStartCheckout = (
    planId: "base" | "core",
    billingInterval: "monthly" | "yearly" = "monthly"
  ) => {
    if (typeof window === "undefined") {
      toast.error("Checkout is unavailable in this environment.");
      return;
    }
    const baseUrl = `${window.location.origin}/billing`;
    startCheckout({
      siteId: portalSiteId,
      planId,
      billingInterval,
      successUrl: `${baseUrl}?state=checkout_success`,
      cancelUrl: `${baseUrl}?state=checkout_canceled`,
    });
  };

  const checkoutVerificationQuery = useQuery({
    ...trpc.stripe.checkoutSession.queryOptions({
      sessionId: checkoutSessionId ?? "",
    }),
    enabled: Boolean(shouldVerifyCheckout && hasAccess),
    retry: false,
  });

  const clearCheckoutParams = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const next = new URLSearchParams(window.location.search);
    next.delete("state");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (checkoutState === "checkout_canceled") {
      toast.info("Checkout cancelled", {
        description: "No changes were applied to your subscription.",
      });
      clearCheckoutParams();
    }
  }, [checkoutState, clearCheckoutParams]);

  useEffect(() => {
    if (checkoutState !== "upgrade_success") {
      return;
    }
    toast.success("Core upgrade submitted", {
      description: "Your Core access will appear after Stripe confirms it.",
    });
    void refetchSubscriptionOverview();
    clearCheckoutParams();
  }, [checkoutState, clearCheckoutParams, refetchSubscriptionOverview]);

  useEffect(() => {
    if (checkoutState === "checkout_success" && !checkoutSessionId) {
      toast.error("Missing checkout session. Please try again.");
      clearCheckoutParams();
    }
  }, [checkoutState, checkoutSessionId, clearCheckoutParams]);

  useEffect(() => {
    if (!checkoutVerificationQuery.isSuccess) {
      return;
    }
    toast.success("Subscription updated", {
      description: "Your billing portal is now available.",
    });
    void refetchSubscriptionOverview();
    clearCheckoutParams();
  }, [
    checkoutVerificationQuery.isSuccess,
    clearCheckoutParams,
    refetchSubscriptionOverview,
  ]);

  useEffect(() => {
    if (!checkoutVerificationQuery.isError) {
      return;
    }
    const message =
      checkoutVerificationQuery.error instanceof Error
        ? checkoutVerificationQuery.error.message
        : "Failed to verify checkout session";
    toast.error(message);
    clearCheckoutParams();
  }, [
    checkoutVerificationQuery.error,
    checkoutVerificationQuery.isError,
    clearCheckoutParams,
  ]);

  if (
    isRoleLoading ||
    subscriptionOverviewQuery.isLoading ||
    !subscriptionOverviewQuery.data
  ) {
    return <SubscriptionOverviewSkeleton />;
  }

  if (!isRoleLoading && !hasAccess) {
    return <AccessRestrictedMessage />;
  }

  const overview = subscriptionOverviewQuery.data;
  const availablePlans = availablePlansQuery.data?.plans ?? [];
  const siteSubscriptions = overview.siteSubscriptions ?? [];
  const includedSites = overview.usage.billedSites ?? 0;
  const isPerSitePricedPlan = isPerActiveSitePlan(overview.plan.id);
  const isPaidPlan = isPerActiveSitePlan(overview.plan.id);
  const downgradeState = getDowngradeToFreeState(overview);
  const activeSiteLimitReached =
    overview.plan.id === "free" &&
    includedSites > 0 &&
    overview.usage.activeSites >= includedSites;
  const includedSitesLabel = isPerSitePricedPlan
    ? `${includedSites} billed`
    : `${includedSites} included`;
  const limitNotice = overview.limitNotice ?? null;
  const primaryPortalSite = siteSubscriptions.find(
    (summary) => summary.billingPortalAvailable
  );
  const primarySubscriptionSummary = primaryPortalSite ?? siteSubscriptions[0];
  const portalSiteId = primarySubscriptionSummary?.siteId ?? undefined;
  const hasActiveSubscription =
    overview.billingState?.hasActivePaidSubscription ||
    checkoutVerificationQuery.isSuccess;
  const hasActivePlanAccess =
    overview.billingState?.hasActivePlanAccess ||
    checkoutVerificationQuery.isSuccess;
  const isTrial = overview.billingState?.isTrial ?? false;
  const pilotBypass = overview.bypass;
  const pilotGrantor = overview.bypassGrantor ?? null;
  const hasScheduledCancellation = Boolean(
    primarySubscriptionSummary?.cancelAtPeriodEnd
  );
  const coreUpsellPlan =
    !pilotBypass?.active &&
    hasActiveSubscription &&
    !hasScheduledCancellation &&
    overview.plan.id === "base"
      ? (availablePlans.find((p) => p.id === "core") ?? null)
      : null;

  let pilotExpiryDisplay: string | null = null;
  if (pilotBypass?.expiresAt) {
    const expiryDate = new Date(pilotBypass.expiresAt);
    if (!Number.isNaN(expiryDate.getTime())) {
      pilotExpiryDisplay = format(expiryDate, "d MMM yyyy");
    }
  }

  let scheduledCancellationDateDisplay: string | null = null;
  if (primarySubscriptionSummary?.currentPeriodEnd) {
    const currentPeriodEnd = new Date(
      primarySubscriptionSummary.currentPeriodEnd
    );
    if (!Number.isNaN(currentPeriodEnd.getTime())) {
      scheduledCancellationDateDisplay = format(currentPeriodEnd, "d MMM yyyy");
    }
  }

  let trialExpiryDisplay: string | null = null;
  if (overview.billingState?.trialExpiresAt) {
    const trialExpiryDate = new Date(overview.billingState.trialExpiresAt);
    if (!Number.isNaN(trialExpiryDate.getTime())) {
      trialExpiryDisplay = format(trialExpiryDate, "d MMM yyyy");
    }
  }

  const handleManageBilling = () => {
    const returnUrl =
      typeof window === "undefined"
        ? "/billing"
        : `${window.location.origin}/billing`;
    const payload = portalSiteId
      ? { siteId: portalSiteId, returnUrl }
      : { returnUrl };
    openBillingPortal(payload);
  };

  const handleUpgradeToCore = () => {
    openCoreUpgrade({
      ...(portalSiteId ? { siteId: portalSiteId } : {}),
    });
  };

  const isConfirmingCheckout =
    shouldVerifyCheckout && checkoutVerificationQuery.isLoading;
  const showPlanSelection =
    !hasActiveSubscription &&
    availablePlans.length > 0 &&
    !isConfirmingCheckout;
  const showPlanSelectionSkeleton =
    !hasActiveSubscription &&
    availablePlansQuery.isLoading &&
    !availablePlansQuery.data &&
    !isConfirmingCheckout;

  return (
    <PageContainer className="max-w-5xl space-y-6">
      <PageHeader
        title="Subscription"
        description="Manage your plan, billing access, and site capacity."
      />
      <div className="space-y-6 max-w-xl mx-auto">
        <PilotBypassAlert
          isActive={Boolean(pilotBypass?.active)}
          pilotGrantorEmail={pilotGrantor?.email ?? null}
          pilotExpiryDisplay={pilotExpiryDisplay}
          pilotReason={pilotBypass?.reason ?? null}
        />
        <ScheduledCancellationAlert
          hasActiveSubscription={hasActiveSubscription}
          hasScheduledCancellation={hasScheduledCancellation}
          scheduledCancellationDateDisplay={scheduledCancellationDateDisplay}
        />
        <CoreTrialAlert
          isVisible={Boolean(isTrial && hasActivePlanAccess)}
          trialExpiryDisplay={trialExpiryDisplay}
        />
      </div>

      {isConfirmingCheckout && (
        <Card className="border max-w-xl mx-auto">
          <CardHeader>
            <CardTitle>Confirming your subscription…</CardTitle>
            <CardDescription>
              Please wait while we verify your subscription details.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {showPlanSelectionSkeleton && <PlanSelectionSkeleton />}

      {showPlanSelection && (
        <div className="space-y-6">
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={cn(
                "text-sm font-medium transition-colors",
                billingCycle === "monthly"
                  ? "text-foreground"
                  : "text-muted-foreground"
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={billingCycle === "yearly"}
              onClick={() =>
                setBillingCycle(
                  billingCycle === "monthly" ? "yearly" : "monthly"
                )
              }
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors",
                billingCycle === "yearly"
                  ? "bg-foreground"
                  : "bg-muted-foreground/30"
              )}
              aria-label="Toggle billing cycle"
            >
              <span
                className={cn(
                  "absolute top-0.5 left-0.5 size-5 rounded-full bg-background shadow transition-transform",
                  billingCycle === "yearly" ? "translate-x-5" : "translate-x-0"
                )}
              />
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("yearly")}
              className={cn(
                "text-sm font-medium transition-colors",
                billingCycle === "yearly"
                  ? "text-foreground"
                  : "text-muted-foreground"
              )}
            >
              Yearly{" "}
              <span className="text-xs text-muted-foreground">(save 15%)</span>
            </button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {availablePlans.map((plan) => {
              const isBase = plan.id === "base";
              const showYearly =
                billingCycle === "yearly" && plan.yearlyPriceDisplay != null;
              const displayPrice = showYearly
                ? plan.yearlyPriceDisplay
                : plan.priceDisplay;
              const planDescription = isBase
                ? "Billed only for active sites, with unlimited workers and site operations tooling. Prices shown ex VAT."
                : "Billed only for active sites, with unlimited workers and Core compliance tooling. Prices shown ex VAT.";
              return (
                <div
                  key={plan.id}
                  className="flex flex-col gap-6 rounded-lg border border-background/15 bg-card-foreground p-8 text-background shadow-[var(--surface-shadow)]"
                >
                  <div className="space-y-4">
                    <Text
                      variant="eyebrow"
                      as="p"
                      className="tracking-widest text-background/60"
                    >
                      {plan.name}
                    </Text>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span
                        className={cn(
                          headingVariants({ variant: "display" }),
                          "text-background"
                        )}
                      >
                        {displayPrice}
                      </span>
                      <span className="text-sm text-background/60">
                        / {describeBillingLabel(plan.id, plan.priceInterval)}{" "}
                        billed {showYearly ? "yearly" : "monthly"}, ex VAT
                      </span>
                    </div>
                    <p className="text-sm text-background/70">
                      {planDescription}
                    </p>
                  </div>
                  <ul className="space-y-3 flex-1">
                    {plan.featureDescriptions.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-background flex-shrink-0" />
                        <span className="text-sm text-background/80">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="inverse"
                    className="w-full"
                    disabled={isCheckoutPending}
                    onClick={() =>
                      handleStartCheckout(
                        plan.id as "base" | "core",
                        billingCycle === "yearly" ? "yearly" : "monthly"
                      )
                    }
                  >
                    {isCheckoutPending
                      ? "Processing..."
                      : `${isTrial ? "Subscribe to" : "Upgrade to"} ${plan.name}`}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!pilotBypass?.active && hasActiveSubscription && (
        <div
          className={cn(
            "grid gap-6 items-start",
            coreUpsellPlan
              ? "max-w-4xl mx-auto md:grid-cols-2"
              : "max-w-xl mx-auto"
          )}
        >
          <Card className="border">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                  {overview.plan.name} plan
                </span>
                <span className="font-semibold">
                  <span
                    className={cn(
                      headingVariants({ variant: "display" }),
                      "mr-2"
                    )}
                  >
                    {overview.plan.price.display}
                  </span>
                  <span>
                    /{" "}
                    {describeBillingLabel(
                      overview.plan.id,
                      overview.plan.billingInterval
                    )}{" "}
                    ex VAT
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <section className="space-y-3">
                <Heading variant="subsection" as="h3">
                  Your plan
                </Heading>
                {limitNotice && (
                  <Alert
                    variant={activeSiteLimitReached ? "destructive" : "default"}
                  >
                    <AlertDescription>{limitNotice}</AlertDescription>
                  </Alert>
                )}
                <dl className="grid gap-3 text-sm">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                    <dt>{describeSiteUsageLabel(overview.plan.id)}</dt>
                    <dd className="font-medium">{includedSitesLabel}</dd>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                    <dt>Workers</dt>
                    <dd className="font-medium flex items-center gap-1">
                      {overview.usage.activeWorkers} /{" "}
                      <span
                        className="text-muted-foreground"
                        dangerouslySetInnerHTML={{
                          __html: formatLimit(
                            overview.plan.limits.maxActiveWorkers
                          ),
                        }}
                      />
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="space-y-3">
                <Heading variant="subsection" as="h3">
                  Included features
                </Heading>
                <ul className="grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
                  {overview.plan.includedFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-muted-foreground" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="space-y-3">
                {hasActiveSubscription ? (
                  <>
                    <Button
                      className="w-full"
                      disabled={isBillingPortalPending}
                      onClick={handleManageBilling}
                    >
                      {isBillingPortalPending
                        ? "Opening portal..."
                        : "Manage subscription"}
                    </Button>
                    {isPaidPlan ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setCancelModalOpen(true)}
                      >
                        Cancel subscription
                      </Button>
                    ) : null}
                    {checkoutVerificationQuery.isSuccess && (
                      <p className="text-xs text-muted-foreground text-center">
                        Subscription updates are processing. Limits will refresh
                        in a few moments.
                      </p>
                    )}
                  </>
                ) : null}
              </section>

              <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {downgradeState.canDowngrade
                        ? "Cancel subscription"
                        : "Downgrade unavailable"}
                    </DialogTitle>
                    <DialogDescription>
                      {downgradeState.canDowngrade
                        ? "Your plan will downgrade to Free at the end of the current billing period."
                        : (downgradeState.blockedReason ??
                          "You cannot downgrade at this time.")}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    {downgradeState.canDowngrade ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setCancelModalOpen(false)}
                        >
                          Keep subscription
                        </Button>
                        <Button
                          variant="destructive"
                          disabled={isDowngradePending}
                          onClick={() => {
                            scheduleDowngrade();
                            setCancelModalOpen(false);
                          }}
                        >
                          {isDowngradePending
                            ? "Scheduling..."
                            : "Downgrade to Free"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setCancelModalOpen(false)}
                        >
                          Close
                        </Button>
                        <Button asChild variant="outline">
                          <Link to="/sites">Go to sites</Link>
                        </Button>
                      </>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {coreUpsellPlan && (
            <div
              id="core-plan"
              className="flex flex-col gap-6 rounded-lg border border-background/15 bg-card-foreground p-8 text-background shadow-[var(--surface-shadow)]"
            >
              <div className="space-y-4">
                <Text
                  variant="eyebrow"
                  as="p"
                  className="tracking-widest text-background/60"
                >
                  {coreUpsellPlan.name}
                </Text>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className={cn(
                      headingVariants({ variant: "display" }),
                      "text-background"
                    )}
                  >
                    {overview.plan.billingInterval === "yearly" &&
                    coreUpsellPlan.yearlyPriceDisplay != null
                      ? coreUpsellPlan.yearlyPriceDisplay
                      : coreUpsellPlan.priceDisplay}
                  </span>
                  <span className="text-sm text-background/60">
                    /{" "}
                    {describeBillingLabel(
                      coreUpsellPlan.id,
                      coreUpsellPlan.priceInterval
                    )}{" "}
                    billed{" "}
                    {overview.plan.billingInterval === "yearly" &&
                    coreUpsellPlan.yearlyPriceDisplay != null
                      ? "yearly"
                      : "monthly"}
                    , ex VAT
                  </span>
                </div>
                <p className="text-sm text-background/70">
                  Billed only for active sites, with unlimited workers and Core
                  compliance tooling. Prices shown ex VAT.
                </p>
              </div>
              <ul className="space-y-3 flex-1">
                {coreUpsellPlan.featureDescriptions.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-background flex-shrink-0" />
                    <span className="text-sm text-background/80">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                variant="inverse"
                className="w-full"
                disabled={isCoreUpgradePending}
                onClick={handleUpgradeToCore}
              >
                {isCoreUpgradePending
                  ? "Preparing upgrade..."
                  : "Upgrade to Core"}
              </Button>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
